'use client';

import Image from 'next/image';

import * as S from './styles';
import { theme } from '@/styles/theme';
import useBreakpoint from '@/hooks/useBreakpoint';
import BreadcrumbPagina from '@/components/molecules/BreadcrumbPagina';
import Heading from '@/components/atoms/Heading';
import Text from '@/components/atoms/Text';
import InformacoesPublicacao from '@/components/molecules/InformacoesPublicacao';
import IndicadorTipoPublicacao from '@/components/molecules/IndicadorTipoPublicacao';
import { HTMLConteudoPublicacao } from '@/components/molecules/HTMLConteudoPublicacao';
import { Fragment, PropsWithChildren, useEffect, useMemo } from 'react';
import PaginaPublicacaoTemplateFallback from '@/components/templates/PaginaPublicacaoTemplate/fallback';

export type PaginaPublicaoTipo = 'Noticia' | 'Critica';

export type PaginaPublicacaoProps = {
  $publicacaoSlug: string;
  $tipoPagina: PaginaPublicaoTipo;
};

export type JsonLDPaginaPublicacao = {
  '@context': string;
  '@type': string;
  image: string[];
  headline: string;
  datePublished: string;
  dateModified: string;
  author: {
    name: string;
    '@type': string;
  }[];
} | null;

export type PaginaPublicacaoTemplateProps = PropsWithChildren<{
  $tipoPublicacao: PaginaPublicaoTipo;
  $carregandoPublicacao: boolean;
  $imagemDestaquePublicacao: string;
  $tituloPublicacao: string;
  $subtituloPublicacao: string;
  $autorPublicacaoImagem?: string;
  $autorPublicacaoNome: string;
  $dataPublicacao: string;
  $tempoLeituraPublicacao: number;
  $conteudoPublicacao: string;
  $jsonLDPaginaPublicacao: JsonLDPaginaPublicacao;
}>;

export default function PaginaPublicacaoTemplate(
  props: PaginaPublicacaoTemplateProps,
) {
  const {
    $tipoPublicacao,
    $carregandoPublicacao,
    $imagemDestaquePublicacao,
    $tituloPublicacao,
    $subtituloPublicacao,
    $autorPublicacaoImagem,
    $autorPublicacaoNome,
    $dataPublicacao,
    $tempoLeituraPublicacao,
    $conteudoPublicacao,
    $jsonLDPaginaPublicacao,
    children,
  } = props;

  const nomeTipoPublicacao =
    $tipoPublicacao === 'Noticia' ? 'Notícias' : 'Críticas';
  const { widthWindow } = useBreakpoint();

  useEffect(() => {
    const s = document.createElement('script');
    s.setAttribute('src', 'https://platform.twitter.com/widgets.js');
    s.setAttribute('async', 'true');
    document.head.appendChild(s);
  }, []);

  const htmlPublicacao = useMemo(() => {
    if (!$conteudoPublicacao) return [];

    function ajustarLinks(conteudo: string): string {
      let ajustado = conteudo.replace(
        /<a([^>]+?)href="https:\/\/www\.chippu\.com\.br\/titulos\/([^"#?:]+)[^"]*"([^>]*)>/g,
        (_match, antesHref, slug, depoisHref) => {
          return `<a${antesHref}href="/ficha/${slug}"${depoisHref}>`;
        },
      );

      ajustado = ajustado.replace(
        /<a[^>]*href="https:\/\/www\.chippu\.com\.br\/noticias\?tag=[^"]*"[^>]*>([\s\S]*?)<\/a>/g,
        (_match, conteudoInterno) => {
          const textoSemTags = conteudoInterno.replace(/<[^>]+>/g, '').trim();
          return `<strong>${textoSemTags}</strong>`;
        },
      );

      return ajustado;
    }
    const conteudoArray = $conteudoPublicacao.split('<p>');

    return conteudoArray.map((value) => `<p>${ajustarLinks(value)}`);
  }, [$conteudoPublicacao]);

  if ($carregandoPublicacao) return <PaginaPublicacaoTemplateFallback />;

  return (
    <Fragment>
      <BreadcrumbPagina $nomePaginaAtual="Notícia" />
      <S.Container>
        <S.ContainerImagemNoticia>
          <Image
            src={$imagemDestaquePublicacao}
            alt={$tituloPublicacao}
            fill
            sizes="100vw"
            style={{
              backgroundColor: theme.colors.neutral.neutral_alpha[3],
              objectFit: 'cover',
            }}
            priority
            unoptimized
          />
        </S.ContainerImagemNoticia>
        <S.ContainerConteudoNoticia>
          <S.BodyContainer>
            <S.BodyWrapper>
              <Heading
                $priority={1}
                $size={7}
                $align="left"
                $weight="bold"
                $highContrast={false}
                $color={theme.tokens.colors.black_contrast}
                $fontFamily="news"
                $text={$tituloPublicacao}
                style={{
                  marginBottom: theme.spacing[3],
                  ...(widthWindow > 1200
                    ? {
                        fontSize: `${
                          theme.typography.font.size[4] +
                          theme.typography.font.size[5]
                        }px`,
                        lineHeight: `${
                          theme.typography.font.size[8] +
                          theme.typography.font.size[4]
                        }px`,
                      }
                    : {
                        lineHeight: `${
                          theme.typography.font.size[7] +
                          theme.typography.font.size[1]
                        }px`,
                      }),
                }}
              />
              <Text
                $size={4}
                $align="left"
                $color={theme.tokens.colors.news.description}
                $weight="medium"
                $highContrast={false}
                $fontFamily="news"
                $text={$subtituloPublicacao}
              />
              <InformacoesPublicacao
                image={$autorPublicacaoImagem}
                author={$autorPublicacaoNome}
                date={$dataPublicacao}
                duration={$tempoLeituraPublicacao}
              />
              <IndicadorTipoPublicacao $tipoPublicacao={nomeTipoPublicacao} />
              <HTMLConteudoPublicacao>
                {htmlPublicacao.map((paragraph, i) => {
                  return (
                    <div
                      dangerouslySetInnerHTML={{ __html: paragraph }}
                      key={i}
                    />
                  );
                })}
              </HTMLConteudoPublicacao>
              {children}
            </S.BodyWrapper>
            {/* <S.SideContainer /> */}
          </S.BodyContainer>
        </S.ContainerConteudoNoticia>
      </S.Container>
      {$jsonLDPaginaPublicacao ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify($jsonLDPaginaPublicacao),
          }}
        />
      ) : null}
    </Fragment>
  );
}
